import { Link } from "react-router-dom";
import { BookOpen, Heart, Gamepad2, Eye, EyeOff, BookMarked, Shield } from "lucide-react";
import { IDS } from "@/lib/testIds";
import { useDyslexiaMode } from "@/hooks/useDyslexiaMode";
import XpBadge from "@/components/XpBadge";
import { useAuth } from "@/context/AuthContext";

export default function Header() {
  const { enabled: dyslexia, toggle: toggleDyslexia } = useDyslexiaMode();
  const { user } = useAuth();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/70 border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid={IDS.nav.logo}
          className="flex items-center gap-2 group"
        >
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-sky-400 to-sky-500 flex items-center justify-center shadow-md group-hover:rotate-6 transition-transform">
            <BookOpen className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-display text-2xl font-semibold tracking-tight text-slate-900">
            Phon<span className="text-sky-500">X</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <button
            data-testid={IDS.nav.basic}
            onClick={() => scrollTo("tracks")}
            className="px-4 py-2 rounded-full text-sm font-bold text-slate-700 hover:bg-slate-100 transition-colors"
          >
            Tracks
          </button>
          <button
            data-testid={IDS.nav.games}
            onClick={() => scrollTo("games")}
            className="px-4 py-2 rounded-full text-sm font-bold text-slate-700 hover:bg-slate-100 transition-colors inline-flex items-center gap-1.5"
          >
            <Gamepad2 className="w-4 h-4" /> Games
          </button>
          <Link
            to="/symbols"
            data-testid="nav-symbols"
            className="px-4 py-2 rounded-full text-sm font-bold text-slate-700 hover:bg-slate-100 transition-colors inline-flex items-center gap-1.5"
          >
            <BookMarked className="w-4 h-4" /> Symbols
          </Link>
          {user?.is_admin && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className="px-4 py-2 rounded-full text-sm font-bold text-emerald-700 hover:bg-emerald-50 transition-colors inline-flex items-center gap-1.5"
            >
              <Shield className="w-4 h-4" /> Admin
            </Link>
          )}
          <button
            data-testid="nav-dyslexia-toggle"
            onClick={toggleDyslexia}
            aria-pressed={dyslexia}
            title={dyslexia ? "Turn off dyslexia-friendly mode" : "Turn on dyslexia-friendly mode"}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-colors inline-flex items-center gap-1.5 ${
              dyslexia
                ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                : "text-slate-700 hover:bg-slate-100"
            }`}
          >
            {dyslexia ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            <span>{dyslexia ? "Dyslexia mode: On" : "Dyslexia mode"}</span>
          </button>
          <button
            data-testid={IDS.nav.donate}
            onClick={() => scrollTo("donate")}
            className="ml-2 px-5 py-2 rounded-full text-sm font-bold bg-rose-400 hover:bg-rose-500 text-white transition-colors shadow-md hover:shadow-lg hover:-translate-y-0.5 duration-200 inline-flex items-center gap-1.5"
          >
            <Heart className="w-4 h-4" fill="currentColor" /> Donate
          </button>
          <div className="ml-2 pl-3 border-l border-slate-200">
            <XpBadge />
          </div>
        </nav>

        <div className="md:hidden flex items-center gap-2">
          <XpBadge compact />
          <Link
            to="/symbols"
            data-testid="nav-symbols-mobile"
            aria-label="Symbols reference"
            className="w-10 h-10 rounded-full inline-flex items-center justify-center bg-slate-100 text-slate-700"
          >
            <BookMarked className="w-4 h-4" />
          </Link>
          <button
            data-testid="nav-dyslexia-toggle-mobile"
            onClick={toggleDyslexia}
            aria-pressed={dyslexia}
            aria-label="Toggle dyslexia mode"
            className={`w-10 h-10 rounded-full inline-flex items-center justify-center transition-colors ${
              dyslexia ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"
            }`}
          >
            {dyslexia ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
          <button
            data-testid={`${IDS.nav.donate}-mobile`}
            onClick={() => scrollTo("donate")}
            className="px-4 py-2 rounded-full text-sm font-bold bg-rose-400 text-white inline-flex items-center gap-1.5"
          >
            <Heart className="w-4 h-4" fill="currentColor" /> Donate
          </button>
        </div>
      </div>
    </header>
  );
}
